import type { CanvasNode } from '@/components/builder/builder-canvas.component';
import type {
  EngageEnrollment,
  EngageSequence,
  EngageStep,
} from '@/store/features/engage.feature';
import { ON_A_STEP } from './sequence-step-leads.util';

/**
 * A sequence's steps are one flat list in depth-first order: a column's steps,
 * then after a branch its Yes arm, then its No arm. `arm_path` names the
 * column: '' is the trunk, 'y' and 'n' are the arms of the trunk's branch. A
 * branch is always the last step of its column. These helpers mirror the
 * API's step tree (`providers/engage/step_tree.py`) for what the canvas draws.
 */

/** The column a step sits in. */
export const armOf = (step: Pick<EngageStep, 'arm_path'>): string =>
  step.arm_path ?? '';

/** A step that does something to the lead, as opposed to a wait or a branch. */
const isTouch = (step: Pick<EngageStep, 'kind'>) =>
  step.kind !== 'wait' && step.kind !== 'branch';

/** Where a new step goes: a column, and the order of the step it follows. */
export interface StepPlace {
  armPath: string;
  /** The order of the step to insert after; -1 is the head of the column. */
  afterOrder: number;
}

/** The steps of each column, in order, by `arm_path`. */
export function columns(
  steps: readonly EngageStep[]
): Record<string, EngageStep[]> {
  const grouped: Record<string, EngageStep[]> = {};
  for (const step of steps) (grouped[armOf(step)] ??= []).push(step);
  return grouped;
}

const ARMS = [
  { key: 'y', label: 'Yes' },
  { key: 'n', label: 'No' },
] as const;

/**
 * The canvas nodes of a column, recursively. A wait folds into the next step
 * of its own column (a wait with none after it keeps a node of its own), and a
 * branch node carries a Yes and a No arm; `armCounts` is the runs under each
 * arm by arm path, written on its label.
 */
export function toCanvasNodes<T>(
  steps: readonly EngageStep[],
  build: (info: {
    step: EngageStep;
    index: number;
    /** The wait folded into this step's header. */
    delay: EngageStep | undefined;
  }) => T,
  armCounts: Readonly<Record<string, number>> = {}
): CanvasNode<T>[] {
  const grouped = columns(steps);
  const nodesOf = (path: string): CanvasNode<T>[] => {
    const column = grouped[path] ?? [];
    return column.flatMap((step, position) => {
      const next = column[position + 1];
      if (step.kind === 'wait' && next && next.kind !== 'wait') return [];
      const previous = column[position - 1];
      const delay =
        step.kind !== 'wait' && previous?.kind === 'wait'
          ? previous
          : undefined;
      const node: CanvasNode<T> = {
        key: step.id,
        item: build({ step, index: steps.indexOf(step), delay }),
      };
      if (step.kind === 'branch')
        node.arms = ARMS.map(({ key, label }) => ({
          key,
          label:
            label +
            (armCounts[path + key] ? ' · ' + armCounts[path + key] : ''),
          nodes: nodesOf(path + key),
        }));
      return [node];
    });
  };
  return nodesOf('');
}

/** The steps a run passes before the spot after `afterOrder` in a column. */
function pathBefore(
  steps: readonly EngageStep[],
  armPath: string,
  afterOrder: number
): EngageStep[] {
  return steps.filter((step) => {
    const path = armOf(step);
    // Every step of a column above is passed; in the column itself, up to the spot.
    return path === armPath
      ? step.order_index <= afterOrder
      : armPath.startsWith(path);
  });
}

/**
 * The steps a run passes before it reaches `step`, through its branches: the
 * earlier steps of its own column and of every column above it, never the
 * other arm.
 */
export function pathTo(
  steps: readonly EngageStep[],
  step: EngageStep
): EngageStep[] {
  return steps
    .slice(0, steps.indexOf(step))
    .filter((item) => armOf(step).startsWith(armOf(item)));
}

/** A send or a task comes before the spot, so a new step there starts with a wait. */
export function followsATouch(
  steps: readonly EngageStep[],
  armPath: string,
  afterOrder: number
): boolean {
  return pathBefore(steps, armPath, afterOrder).some(isTouch);
}

/** The step a new one lands in front of: the next in the same column. */
export function stepAfter(
  steps: readonly EngageStep[],
  armPath: string,
  afterOrder: number
): EngageStep | undefined {
  return steps.find(
    (step) => armOf(step) === armPath && step.order_index > afterOrder
  );
}

/**
 * The spot directly before `step` in its own column: where a wait that
 * delays it goes. The step before it in the flat list may be in another arm.
 */
export function insertBefore(
  steps: readonly EngageStep[],
  step: EngageStep
): StepPlace {
  const earlier = steps.filter(
    (item) => armOf(item) === armOf(step) && item.order_index < step.order_index
  );
  return {
    armPath: armOf(step),
    afterOrder: earlier.length ? earlier[earlier.length - 1].order_index : -1,
  };
}

/**
 * Where a step added "at the end" goes: after the last step in its column, or
 * at the head of the No arm of a last branch. A branch ends its column, so
 * nothing can follow it there.
 */
export function endPosition(steps: readonly EngageStep[]): StepPlace {
  const last = steps[steps.length - 1];
  if (!last) return { armPath: '', afterOrder: -1 };
  return last.kind === 'branch'
    ? { armPath: armOf(last) + 'n', afterOrder: -1 }
    : { armPath: armOf(last), afterOrder: last.order_index };
}

/**
 * The flat position of a new step: after the step with order `afterOrder`,
 * else at the head of the column (right after its branch for the Yes arm,
 * after the Yes arm's last step for the No arm).
 */
export function insertPosition(
  steps: readonly EngageStep[],
  armPath: string,
  afterOrder: number
): number {
  if (afterOrder >= 0) {
    const index = steps.findIndex((step) => step.order_index === afterOrder);
    if (index >= 0) return index + 1;
  }
  if (armPath === '') return 0;
  const parent = armPath.slice(0, -1);
  let branch = -1;
  steps.forEach((step, index) => {
    if (step.kind === 'branch' && armOf(step) === parent) branch = index;
  });
  if (branch < 0) return steps.length;
  let end = branch + 1;
  // The Yes arm, and any columns under it, sit between the branch and the No arm.
  if (armPath.endsWith('n'))
    while (end < steps.length && armOf(steps[end]).startsWith(parent + 'y'))
      end += 1;
  return end;
}

/** Every step in the columns under a branch. */
export function subtree(steps: readonly EngageStep[], branch: EngageStep) {
  const path = armOf(branch);
  return steps.filter(
    (step) => armOf(step).length > path.length && armOf(step).startsWith(path)
  );
}

/** What deleting a branch asks first, or null when no step hangs off it. */
export function branchDeleteTitle(
  steps: readonly EngageStep[],
  branch: EngageStep
): string | null {
  const count = subtree(steps, branch).filter(
    (step) => step.kind !== 'wait'
  ).length;
  return count
    ? 'Delete branch and ' + count + (count === 1 ? ' step' : ' steps')
    : null;
}

/**
 * Runs standing in each arm, by arm path, from the position they report in the
 * published version. A run in a column under another counts under both. A
 * draft, or a run of another version, has a position in steps not on screen.
 */
export function armLeadCounts(
  steps: readonly EngageStep[],
  enrollments: readonly EngageEnrollment[],
  sequence: Pick<EngageSequence, 'version' | 'draft'>
): Record<string, number> {
  const counts: Record<string, number> = {};
  if (sequence.draft) return counts;
  for (const run of enrollments) {
    if (
      !ON_A_STEP.has(run.status) ||
      run.version !== sequence.version ||
      run.step_index == null
    )
      continue;
    const step = steps.find((item) => item.order_index === run.step_index);
    const path = step ? armOf(step) : '';
    for (let end = 1; end <= path.length; end += 1) {
      const arm = path.slice(0, end);
      counts[arm] = (counts[arm] ?? 0) + 1;
    }
  }
  return counts;
}
